import JSZip from 'jszip';

/**
 * Shared TypeScript type definitions for Feedbacker
 * Core interfaces and types used across the React widget and Chrome extension
 */
// Error handling
var ErrorType;
(function (ErrorType) {
    ErrorType["STORAGE_FULL"] = "STORAGE_FULL";
    ErrorType["STORAGE_CORRUPT"] = "STORAGE_CORRUPT";
    ErrorType["SCREENSHOT_FAILED"] = "SCREENSHOT_FAILED";
    ErrorType["EXPORT_FAILED"] = "EXPORT_FAILED";
    ErrorType["DETECTION_FAILED"] = "DETECTION_FAILED";
})(ErrorType || (ErrorType = {}));

/**
 * Custom logger utility for Feedbacker
 * Provides environment-aware logging that can be disabled in production
 */
var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["DEBUG"] = 0] = "DEBUG";
    LogLevel[LogLevel["INFO"] = 1] = "INFO";
    LogLevel[LogLevel["WARN"] = 2] = "WARN";
    LogLevel[LogLevel["ERROR"] = 3] = "ERROR";
    LogLevel[LogLevel["NONE"] = 4] = "NONE";
})(LogLevel || (LogLevel = {}));
class Logger {
    config;
    isDevelopment;
    isTest;
    constructor() {
        this.isDevelopment = "development" === 'development';
        this.isTest = "development" === 'test';
        // Default configuration
        this.config = {
            level: this.getDefaultLogLevel(),
            prefix: '[Feedbacker]',
            enabled: this.isDevelopment || this.isTest,
            timestamp: false
        };
        // Allow runtime configuration via window object in browser
        if (typeof window !== 'undefined' && window.__FEEDBACKER_LOG_LEVEL__) {
            this.config.level = window.__FEEDBACKER_LOG_LEVEL__;
        }
    }
    getDefaultLogLevel() {
        return LogLevel.DEBUG; // Everything in development
    }
    shouldLog(level) {
        return this.config.enabled && level >= this.config.level;
    }
    formatMessage(level, message, ...args) {
        const timestamp = this.config.timestamp ? `[${new Date().toISOString()}] ` : '';
        const levelStr = `[${level}]`;
        return [`${timestamp}${this.config.prefix} ${levelStr} ${message}`, ...args];
    }
    debug(message, ...args) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.log(...this.formatMessage('DEBUG', message, ...args));
        }
    }
    info(message, ...args) {
        if (this.shouldLog(LogLevel.INFO)) {
            // eslint-disable-next-line no-console
            console.log(...this.formatMessage('INFO', message, ...args));
        }
    }
    log(message, ...args) {
        this.info(message, ...args);
    }
    warn(message, ...args) {
        if (this.shouldLog(LogLevel.WARN)) {
            // eslint-disable-next-line no-console
            console.warn(...this.formatMessage('WARN', message, ...args));
        }
    }
    error(message, ...args) {
        if (this.shouldLog(LogLevel.ERROR)) {
            // eslint-disable-next-line no-console
            console.error(...this.formatMessage('ERROR', message, ...args));
        }
    }
    /**
     * Group related logs together (only in development)
     */
    group(label) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.group(`${this.config.prefix} ${label}`);
        }
    }
    groupEnd() {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.groupEnd();
        }
    }
    /**
     * Create a table output (only in development)
     */
    table(data) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.table(data);
        }
    }
    /**
     * Time measurement utilities
     */
    time(label) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.time(`${this.config.prefix} ${label}`);
        }
    }
    timeEnd(label) {
        if (this.shouldLog(LogLevel.DEBUG)) {
            // eslint-disable-next-line no-console
            console.timeEnd(`${this.config.prefix} ${label}`);
        }
    }
    /**
     * Configure the logger at runtime
     */
    configure(config) {
        this.config = { ...this.config, ...config };
    }
    /**
     * Enable or disable logging
     */
    enable() {
        this.config.enabled = true;
    }
    disable() {
        this.config.enabled = false;
    }
    /**
     * Set log level
     */
    setLevel(level) {
        this.config.level = level;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return { ...this.config };
    }
}
// Create singleton instance
const logger = new Logger();

/**
 * FeedbackEventEmitter - Framework-agnostic event bus for Feedbacker
 * Provides pub/sub pattern for component interactions
 */
class FeedbackEventEmitter {
    listeners = new Map();
    onceListeners = new Map();
    emit(type, payload) {
        // Regular listeners
        const typeListeners = this.listeners.get(type);
        if (typeListeners) {
            typeListeners.forEach((listener) => {
                try {
                    listener(payload);
                }
                catch (error) {
                    logger.error(`Event listener error for ${type}:`, error);
                }
            });
        }
        // Once listeners
        const onceTypeListeners = this.onceListeners.get(type);
        if (onceTypeListeners) {
            onceTypeListeners.forEach((listener) => {
                try {
                    listener(payload);
                }
                catch (error) {
                    logger.error(`Once event listener error for ${type}:`, error);
                }
            });
            // Clear once listeners after execution
            this.onceListeners.delete(type);
        }
    }
    on(type, listener) {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, new Set());
        }
        const typeListeners = this.listeners.get(type);
        typeListeners.add(listener);
        // Return cleanup function
        return () => {
            typeListeners.delete(listener);
            if (typeListeners.size === 0) {
                this.listeners.delete(type);
            }
        };
    }
    once(type, listener) {
        if (!this.onceListeners.has(type)) {
            this.onceListeners.set(type, new Set());
        }
        const onceTypeListeners = this.onceListeners.get(type);
        onceTypeListeners.add(listener);
        // Return cleanup function
        return () => {
            onceTypeListeners.delete(listener);
            if (onceTypeListeners.size === 0) {
                this.onceListeners.delete(type);
            }
        };
    }
    clear() {
        this.listeners.clear();
        this.onceListeners.clear();
    }
}

/**
 * Validation utilities for feedback data
 * Ensures data integrity and type safety
 */
/**
 * Validate a complete feedback object
 */
function validateFeedback(feedback) {
    const errors = [];
    // Required fields validation
    if (!feedback.id || typeof feedback.id !== 'string') {
        errors.push('Feedback ID is required and must be a string');
    }
    if (!feedback.componentName || typeof feedback.componentName !== 'string') {
        errors.push('Component name is required and must be a string');
    }
    if (!feedback.comment || typeof feedback.comment !== 'string' || feedback.comment.trim() === '') {
        errors.push('Comment is required and must be a non-empty string');
    }
    if (!feedback.timestamp || typeof feedback.timestamp !== 'string') {
        errors.push('Timestamp is required and must be a string');
    }
    else {
        // Validate timestamp format
        const timestamp = new Date(feedback.timestamp);
        if (isNaN(timestamp.getTime())) {
            errors.push('Timestamp must be a valid date string');
        }
    }
    if (!feedback.url || typeof feedback.url !== 'string') {
        errors.push('URL is required and must be a string');
    }
    else {
        // Basic URL validation
        try {
            new URL(feedback.url);
        }
        catch {
            errors.push('URL must be a valid URL format');
        }
    }
    // Optional fields validation
    if (feedback.componentPath !== undefined) {
        if (!Array.isArray(feedback.componentPath)) {
            errors.push('Component path must be an array');
        }
        else {
            const invalidPaths = feedback.componentPath.filter((path) => typeof path !== 'string');
            if (invalidPaths.length > 0) {
                errors.push('All component path items must be strings');
            }
        }
    }
    if (feedback.screenshot !== undefined) {
        if (typeof feedback.screenshot !== 'string') {
            errors.push('Screenshot must be a string');
        }
        else {
            // Validate base64 data URL format
            if (!feedback.screenshot.startsWith('data:image/')) {
                errors.push('Screenshot must be a valid data URL');
            }
        }
    }
    if (feedback.browserInfo !== undefined) {
        const browserValidation = validateBrowserInfo(feedback.browserInfo);
        if (!browserValidation.valid) {
            errors.push(...browserValidation.errors.map((err) => `Browser info: ${err}`));
        }
    }
    if (feedback.metadata !== undefined) {
        if (typeof feedback.metadata !== 'object' ||
            feedback.metadata === null ||
            Array.isArray(feedback.metadata)) {
            errors.push('Metadata must be an object');
        }
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Validate a draft object
 */
function validateDraft(draft) {
    const errors = [];
    if (!draft.componentInfo) {
        errors.push('Component info is required');
    }
    else {
        const componentValidation = validateComponentInfo(draft.componentInfo);
        if (!componentValidation.valid) {
            errors.push(...componentValidation.errors.map((err) => `Component info: ${err}`));
        }
    }
    if (!draft.comment || typeof draft.comment !== 'string') {
        errors.push('Comment is required and must be a string');
    }
    if (!draft.createdAt || typeof draft.createdAt !== 'string') {
        errors.push('Created date is required and must be a string');
    }
    else {
        const createdAt = new Date(draft.createdAt);
        if (isNaN(createdAt.getTime())) {
            errors.push('Created date must be a valid date string');
        }
    }
    if (!draft.updatedAt || typeof draft.updatedAt !== 'string') {
        errors.push('Updated date is required and must be a string');
    }
    else {
        const updatedAt = new Date(draft.updatedAt);
        if (isNaN(updatedAt.getTime())) {
            errors.push('Updated date must be a valid date string');
        }
    }
    if (draft.screenshot !== undefined) {
        if (typeof draft.screenshot !== 'string') {
            errors.push('Screenshot must be a string');
        }
        else {
            if (!draft.screenshot.startsWith('data:image/')) {
                errors.push('Screenshot must be a valid data URL');
            }
        }
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Validate component info object
 */
function validateComponentInfo(componentInfo) {
    const errors = [];
    if (!componentInfo.name || typeof componentInfo.name !== 'string') {
        errors.push('Component name is required and must be a string');
    }
    if (!componentInfo.path || !Array.isArray(componentInfo.path)) {
        errors.push('Component path is required and must be an array');
    }
    else {
        const invalidPaths = componentInfo.path.filter((path) => typeof path !== 'string');
        if (invalidPaths.length > 0) {
            errors.push('All component path items must be strings');
        }
    }
    if (!componentInfo.element) {
        errors.push('Component element is required');
    }
    else {
        // Check if it's a DOM element (in browser environment)
        if (typeof window !== 'undefined' && !(componentInfo.element instanceof HTMLElement)) {
            errors.push('Component element must be an HTMLElement');
        }
    }
    if (componentInfo.props !== undefined) {
        if (typeof componentInfo.props !== 'object' ||
            componentInfo.props === null ||
            Array.isArray(componentInfo.props)) {
            errors.push('Component props must be an object');
        }
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Validate browser info object
 */
function validateBrowserInfo(browserInfo) {
    const errors = [];
    if (!browserInfo.userAgent || typeof browserInfo.userAgent !== 'string') {
        errors.push('User agent is required and must be a string');
    }
    if (!browserInfo.viewport) {
        errors.push('Viewport info is required');
    }
    else {
        if (typeof browserInfo.viewport !== 'object' || browserInfo.viewport === null) {
            errors.push('Viewport must be an object');
        }
        else {
            if (typeof browserInfo.viewport.width !== 'number' || browserInfo.viewport.width <= 0) {
                errors.push('Viewport width must be a positive number');
            }
            if (typeof browserInfo.viewport.height !== 'number' || browserInfo.viewport.height <= 0) {
                errors.push('Viewport height must be a positive number');
            }
        }
    }
    if (!browserInfo.platform || typeof browserInfo.platform !== 'string') {
        errors.push('Platform is required and must be a string');
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Validate feedback store structure
 */
function validateStorageData(data) {
    try {
        if (!data || typeof data !== 'object') {
            return false;
        }
        // Check required fields
        if (typeof data.version !== 'string') {
            return false;
        }
        if (!Array.isArray(data.feedbacks)) {
            return false;
        }
        // Validate each feedback item (basic validation)
        for (const feedback of data.feedbacks) {
            if (!feedback.id || !feedback.componentName || !feedback.comment) {
                return false;
            }
        }
        return true;
    }
    catch (error) {
        return false;
    }
}
/**
 * Validate comment content
 */
function validateComment(comment) {
    const errors = [];
    if (typeof comment !== 'string') {
        errors.push('Comment must be a string');
        return { valid: false, errors };
    }
    const trimmed = comment.trim();
    if (trimmed.length === 0) {
        errors.push('Comment cannot be empty');
    }
    if (trimmed.length > 10000) {
        errors.push('Comment is too long (maximum 10000 characters)');
    }
    // Check for potentially harmful content
    const suspiciousPatterns = [
        /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
        /javascript:/gi,
        /data:text\/html/gi,
        /vbscript:/gi
    ];
    for (const pattern of suspiciousPatterns) {
        if (pattern.test(comment)) {
            errors.push('Comment contains potentially harmful content');
            break;
        }
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Validate screenshot data URL
 */
function validateScreenshot(screenshot) {
    const errors = [];
    if (typeof screenshot !== 'string') {
        errors.push('Screenshot must be a string');
        return { valid: false, errors };
    }
    if (!screenshot.startsWith('data:image/')) {
        errors.push('Screenshot must be a data URL with image MIME type');
    }
    // Check if it's a reasonable size (not too large)
    const estimatedSize = screenshot.length * 0.75; // Base64 overhead
    const maxSize = 5 * 1024 * 1024; // 5MB limit
    if (estimatedSize > maxSize) {
        errors.push('Screenshot is too large (maximum 5MB)');
    }
    // Validate base64 format
    const dataUrlPattern = /^data:image\/(png|jpeg|jpg|gif|webp);base64,([A-Za-z0-9+/=]+)$/;
    if (!dataUrlPattern.test(screenshot)) {
        errors.push('Screenshot has invalid data URL format');
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Validate feedback ID format
 */
function validateFeedbackId(id) {
    const errors = [];
    if (typeof id !== 'string') {
        errors.push('ID must be a string');
        return { valid: false, errors };
    }
    if (id.trim().length === 0) {
        errors.push('ID cannot be empty');
    }
    if (id.length > 100) {
        errors.push('ID is too long (maximum 100 characters)');
    }
    // Check for valid ID format (alphanumeric, hyphens, underscores)
    const idPattern = /^[a-zA-Z0-9_-]+$/;
    if (!idPattern.test(id)) {
        errors.push('ID contains invalid characters (only alphanumeric, hyphens, and underscores allowed)');
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Quick validation - returns boolean only
 */
function isValidFeedback(feedback) {
    return validateFeedback(feedback).valid;
}
function isValidDraft(draft) {
    return validateDraft(draft).valid;
}
function isValidComment(comment) {
    return validateComment(comment).valid;
}
function isValidScreenshot(screenshot) {
    return validateScreenshot(screenshot).valid;
}

/**
 * Data sanitization utilities for feedback data
 * Ensures data safety and prevents XSS attacks
 */
/**
 * Sanitize a complete feedback object
 */
function sanitizeFeedback(feedback) {
    return {
        id: sanitizeString(feedback.id),
        componentName: sanitizeString(feedback.componentName),
        componentPath: sanitizeArray(feedback.componentPath, sanitizeString),
        comment: sanitizeComment(feedback.comment),
        screenshot: feedback.screenshot ? sanitizeDataUrl(feedback.screenshot) : undefined,
        url: sanitizeUrl(feedback.url),
        timestamp: sanitizeTimestamp(feedback.timestamp),
        browserInfo: sanitizeBrowserInfo(feedback.browserInfo),
        htmlSnippet: feedback.htmlSnippet ? sanitizeString(feedback.htmlSnippet, 5000) : undefined,
        metadata: feedback.metadata ? sanitizeMetadata(feedback.metadata) : undefined
    };
}
/**
 * Sanitize a draft object
 */
function sanitizeDraft(draft) {
    return {
        componentInfo: sanitizeComponentInfo(draft.componentInfo),
        comment: sanitizeComment(draft.comment),
        screenshot: draft.screenshot ? sanitizeDataUrl(draft.screenshot) : undefined,
        createdAt: sanitizeTimestamp(draft.createdAt),
        updatedAt: sanitizeTimestamp(draft.updatedAt)
    };
}
/**
 * Sanitize component info object
 */
function sanitizeComponentInfo(componentInfo) {
    return {
        name: sanitizeString(componentInfo.name),
        path: sanitizeArray(componentInfo.path, sanitizeString),
        element: componentInfo.element, // DOM elements are not sanitized
        htmlSnippet: componentInfo.htmlSnippet
            ? sanitizeString(componentInfo.htmlSnippet, 5000)
            : undefined,
        props: componentInfo.props ? sanitizeProps(componentInfo.props) : undefined,
        fiber: componentInfo.fiber // React fiber is not sanitized
    };
}
/**
 * Sanitize browser info object
 */
function sanitizeBrowserInfo(browserInfo) {
    return {
        userAgent: sanitizeString(browserInfo.userAgent),
        viewport: {
            width: sanitizeNumber(browserInfo.viewport.width, 1, 10000),
            height: sanitizeNumber(browserInfo.viewport.height, 1, 10000)
        },
        platform: sanitizeString(browserInfo.platform || '')
    };
}
/**
 * Sanitize a basic string by removing harmful content
 */
function sanitizeString(value, maxLength = 1000) {
    if (typeof value !== 'string') {
        return String(value || '').substring(0, maxLength);
    }
    return (value
        .trim()
        .substring(0, maxLength)
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
        // eslint-disable-next-line no-control-regex
        .replace(/\u0000/g, '')); // Remove null characters
}
/**
 * Sanitize comment text with enhanced security
 */
function sanitizeComment(comment) {
    if (typeof comment !== 'string') {
        return '';
    }
    let sanitized = comment.trim();
    // Length limit
    if (sanitized.length > 10000) {
        sanitized = sanitized.substring(0, 10000);
    }
    // Remove potentially harmful content
    sanitized = sanitized
        // Remove script tags
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        // Remove javascript: protocols
        .replace(/javascript:/gi, '')
        // Remove vbscript: protocols
        .replace(/vbscript:/gi, '')
        // Remove data: URLs that could contain HTML
        .replace(/data:text\/html/gi, '')
        // Remove on* event handlers
        .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
        // Remove form-related elements
        .replace(/<\s*\/?\s*(form|input|textarea|button|select|option)\b[^>]*>/gi, '')
        // Remove iframe and object elements
        .replace(/<\s*\/?\s*(iframe|object|embed|applet)\b[^>]*>/gi, '')
        // Remove link and style elements
        .replace(/<\s*\/?\s*(link|style)\b[^>]*>/gi, '')
        // Remove meta elements
        .replace(/<\s*meta\b[^>]*>/gi, '')
        // Clean up control characters except newlines and tabs
        // eslint-disable-next-line no-control-regex
        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
    return sanitized;
}
/**
 * Sanitize a URL
 */
function sanitizeUrl(url) {
    const fallbackUrl = typeof window !== 'undefined' ? window.location.href : 'https://unknown.local';
    if (typeof url !== 'string') {
        return fallbackUrl;
    }
    try {
        const parsed = new URL(url);
        // Only allow http and https protocols
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return fallbackUrl;
        }
        // Limit URL length
        if (url.length > 2000) {
            return fallbackUrl;
        }
        return url;
    }
    catch (error) {
        // Invalid URL, fallback
        return fallbackUrl;
    }
}
/**
 * Sanitize timestamp
 */
function sanitizeTimestamp(timestamp) {
    if (typeof timestamp !== 'string') {
        return new Date().toISOString();
    }
    try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
            return new Date().toISOString();
        }
        // Ensure timestamp is not in the future (with 1 minute tolerance)
        const now = new Date();
        const maxAllowed = new Date(now.getTime() + 60000);
        if (date > maxAllowed) {
            return now.toISOString();
        }
        // Ensure timestamp is not too old (more than 10 years)
        const minAllowed = new Date(now.getTime() - 10 * 365 * 24 * 60 * 60 * 1000);
        if (date < minAllowed) {
            return now.toISOString();
        }
        return date.toISOString();
    }
    catch (error) {
        return new Date().toISOString();
    }
}
/**
 * Sanitize data URL (for screenshots)
 */
function sanitizeDataUrl(dataUrl) {
    if (typeof dataUrl !== 'string') {
        return '';
    }
    // Check if it's a valid data URL format
    const dataUrlPattern = /^data:image\/(png|jpeg|jpg|gif|webp);base64,([A-Za-z0-9+/=]+)$/;
    if (!dataUrlPattern.test(dataUrl)) {
        return '';
    }
    // Check size (approximate, base64 is ~1.33x the binary size)
    const estimatedSize = dataUrl.length * 0.75;
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (estimatedSize > maxSize) {
        return '';
    }
    return dataUrl;
}
/**
 * Sanitize a number with bounds
 */
function sanitizeNumber(value, min = 0, max = Number.MAX_SAFE_INTEGER) {
    if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
        return min;
    }
    return Math.max(min, Math.min(max, Math.floor(value)));
}
/**
 * Sanitize an array by applying a sanitizer function to each element
 */
function sanitizeArray(array, sanitizer) {
    if (!Array.isArray(array)) {
        return [];
    }
    return array
        .slice(0, 100) // Limit array size
        .map(sanitizer)
        .filter((item) => item !== null && item !== undefined);
}
/**
 * Sanitize component props object
 */
function sanitizeProps(props) {
    if (!props || typeof props !== 'object' || Array.isArray(props)) {
        return {};
    }
    const sanitized = {};
    const maxProps = 50; // Limit number of props
    let propCount = 0;
    for (const [key, value] of Object.entries(props)) {
        if (propCount >= maxProps) {
            break;
        }
        const sanitizedKey = sanitizeString(key, 100);
        if (sanitizedKey) {
            sanitized[sanitizedKey] = sanitizeValue(value);
            propCount++;
        }
    }
    return sanitized;
}
/**
 * Sanitize metadata object
 */
function sanitizeMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
        return {};
    }
    const sanitized = {};
    const maxEntries = 20; // Limit number of metadata entries
    let entryCount = 0;
    for (const [key, value] of Object.entries(metadata)) {
        if (entryCount >= maxEntries) {
            break;
        }
        const sanitizedKey = sanitizeString(key, 50);
        if (sanitizedKey) {
            sanitized[sanitizedKey] = sanitizeValue(value);
            entryCount++;
        }
    }
    return sanitized;
}
/**
 * Sanitize a generic value based on its type
 */
function sanitizeValue(value) {
    if (value === null || value === undefined) {
        return value;
    }
    switch (typeof value) {
        case 'string':
            return sanitizeString(value, 500);
        case 'number':
            return sanitizeNumber(value);
        case 'boolean':
            return Boolean(value);
        case 'object':
            if (Array.isArray(value)) {
                return value.slice(0, 10).map(sanitizeValue); // Limit array size
            }
            if (value instanceof Date) {
                return value.toISOString();
            }
            // For other objects, limit depth to prevent infinite recursion
            return sanitizeShallowObject(value);
        default:
            return String(value).substring(0, 100);
    }
}
/**
 * Sanitize an object with limited depth
 */
function sanitizeShallowObject(obj) {
    const sanitized = {};
    let entryCount = 0;
    for (const [key, value] of Object.entries(obj)) {
        if (entryCount >= 10) {
            break;
        } // Limit number of properties
        const sanitizedKey = sanitizeString(key, 50);
        if (sanitizedKey) {
            // Only sanitize primitive values to avoid deep recursion
            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                sanitized[sanitizedKey] = sanitizeValue(value);
            }
            else {
                sanitized[sanitizedKey] = '[Object]';
            }
            entryCount++;
        }
    }
    return sanitized;
}
/**
 * Remove potentially harmful HTML/JS from text
 */
function stripHtmlTags(text) {
    if (typeof text !== 'string') {
        return '';
    }
    return text
        .replace(/<[^>]*>/g, '') // Remove all HTML tags
        .replace(/&[#\w]+;/g, '') // Remove HTML entities
        .trim();
}
/**
 * Escape HTML characters in text
 */
function escapeHtml(text) {
    if (typeof text !== 'string') {
        return '';
    }
    const htmlEscapes = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '/': '&#x2F;'
    };
    // eslint-disable-next-line no-useless-escape
    return text.replace(/[&<>"'\/]/g, (match) => htmlEscapes[match] || match);
}
/**
 * Generate a safe filename from a string
 */
function sanitizeFilename(filename) {
    if (typeof filename !== 'string') {
        return 'feedback';
    }
    return filename
        .trim()
        .substring(0, 100)
        .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace invalid characters
        .replace(/_+/g, '_') // Collapse multiple underscores
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores
}

/**
 * Date utility functions for formatting timestamps
 */
/**
 * Format distance from now (like "2 hours ago", "3 days ago")
 */
const formatDistanceToNow = (date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInMinutes < 1) {
        return 'just now';
    }
    else if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    }
    else if (diffInHours < 24) {
        return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    }
    else if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    }
    else {
        // For older dates, show the actual date
        return date.toLocaleDateString();
    }
};
/**
 * Format a date to a readable string
 */
const formatDate = (date) => {
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};
/**
 * Format a date to ISO string for storage
 */
const formatDateForStorage = (date) => {
    return date.toISOString();
};

/**
 * HTML Snippet utility
 * Captures the first and last few lines of an element's outerHTML
 */
/**
 * Capture HTML snippet showing first and last lines of element
 */
function captureHtmlSnippet(element, linesCount = 3) {
    try {
        // Guard against deep-cloning very large subtrees which blocks the main thread
        const MAX_CLONE_NODES = 500;
        const descendantCount = element.querySelectorAll('*').length;
        if (descendantCount > MAX_CLONE_NODES) {
            const tag = element.tagName.toLowerCase();
            const attrs = Array.from(element.attributes)
                .slice(0, 5)
                .map((a) => `${a.name}="${a.value}"`)
                .join(' ');
            return `<${tag} ${attrs}><!-- ${descendantCount} child elements truncated --></${tag}>`;
        }
        // Clone the element to avoid modifying the original
        const clone = element.cloneNode(true);
        // Remove script tags and style tags for cleaner output
        clone.querySelectorAll('script, style').forEach((el) => el.remove());
        // Get the outerHTML and format it
        const outerHTML = clone.outerHTML;
        // Pretty format the HTML
        const formatted = prettifyHtml(outerHTML);
        const lines = formatted.split('\n').filter((line) => line.trim().length > 0);
        if (lines.length <= linesCount * 2) {
            // If total lines are less than or equal to what we want to show, return all
            return lines.join('\n');
        }
        // Get first N lines and last N lines
        const firstLines = lines.slice(0, linesCount);
        const lastLines = lines.slice(-linesCount);
        // Combine with ellipsis in between
        return [...firstLines, '  ...', ...lastLines].join('\n');
    }
    catch (error) {
        logger.error('Failed to capture HTML snippet:', error);
        return '';
    }
}
/**
 * Pretty print HTML with proper indentation
 */
function prettifyHtml(html) {
    try {
        let formatted = '';
        let indent = 0;
        // Add newlines and indentation
        html = html
            // Add newline before opening tags
            // eslint-disable-next-line no-useless-escape
            .replace(/(<[^\/][^>]*>)/g, '\n$1')
            // Add newline after closing tags
            .replace(/(<\/[^>]+>)/g, '$1\n')
            // Remove empty lines
            .replace(/\n\s*\n/g, '\n');
        const lines = html.split('\n').filter((line) => line.trim());
        for (const line of lines) {
            const trimmed = line.trim();
            // Skip empty lines
            if (!trimmed) {
                continue;
            }
            // Closing tag - decrease indent before
            if (trimmed.startsWith('</')) {
                indent = Math.max(0, indent - 1);
            }
            // Add the indented line
            formatted += '  '.repeat(indent) + trimmed + '\n';
            // Opening tag (not self-closing) - increase indent after
            if (trimmed.startsWith('<') &&
                !trimmed.startsWith('</') &&
                !trimmed.endsWith('/>') &&
                !trimmed.includes('</')) {
                // Check if it's not a void element
                const voidElements = [
                    'img',
                    'br',
                    'hr',
                    'input',
                    'meta',
                    'link',
                    'area',
                    'base',
                    'col',
                    'embed',
                    'source',
                    'track',
                    'wbr'
                ];
                const tagName = trimmed.match(/<([a-zA-Z0-9]+)/)?.[1]?.toLowerCase();
                if (!voidElements.includes(tagName || '')) {
                    indent++;
                }
            }
        }
        return formatted.trim();
    }
    catch (error) {
        logger.error('Failed to prettify HTML:', error);
        return html;
    }
}
/**
 * Format HTML snippet for better readability
 */
function formatHtmlSnippet(html) {
    try {
        // The HTML is already formatted by captureHtmlSnippet
        // Just ensure consistent indentation for the ellipsis
        const lines = html.split('\n');
        return lines
            .map((line) => {
            // Keep ellipsis centered without indentation
            if (line.trim() === '...') {
                return '\n  ...\n';
            }
            return line;
        })
            .join('\n')
            .trim();
    }
    catch (error) {
        // If formatting fails, return original
        return html;
    }
}

/**
 * Data migration utilities for handling version changes in stored data
 */
/**
 * Get default browser info, safe for non-browser environments
 */
function getDefaultBrowserInfo() {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
        return {
            userAgent: navigator.userAgent || 'Unknown',
            viewport: {
                width: window.innerWidth || 1024,
                height: window.innerHeight || 768
            },
            platform: navigator.platform || 'Unknown'
        };
    }
    return {
        userAgent: 'Unknown',
        viewport: { width: 1024, height: 768 },
        platform: 'Unknown'
    };
}
/**
 * Get current URL, safe for non-browser environments
 */
function getCurrentUrl() {
    if (typeof window !== 'undefined') {
        return window.location.href;
    }
    return 'https://unknown.local';
}
/**
 * Migration registry mapping version patterns to migration functions
 */
const migrations = {
    '0.1.0': migrateFrom_0_1_0,
    '0.2.0': migrateFrom_0_2_0,
    legacy: migrateLegacyData
};
/**
 * Main migration function that handles data migration between versions
 */
async function migrateData(oldData, targetVersion) {
    try {
        if (!oldData) {
            return null;
        }
        // If no version is present, try legacy migration
        if (!oldData.version) {
            logger.info('No version found, attempting legacy migration');
            return migrations.legacy(oldData);
        }
        const sourceVersion = oldData.version;
        // If versions match, no migration needed (but validate structure)
        if (sourceVersion === targetVersion) {
            return validateAndNormalize(oldData);
        }
        // Determine which migration to use
        const migrationKey = getMigrationKey(sourceVersion);
        const migrationFn = migrations[migrationKey];
        if (!migrationFn) {
            logger.warn(`No migration available for version ${sourceVersion}`);
            return null;
        }
        logger.info(`Migrating data from ${sourceVersion} to ${targetVersion}`);
        const migratedData = migrationFn(oldData);
        if (migratedData) {
            // Update version to target
            migratedData.version = targetVersion;
            return validateAndNormalize(migratedData);
        }
        return null;
    }
    catch (error) {
        logger.error('Migration error:', error);
        return null;
    }
}
/**
 * Get migration key based on source version
 */
function getMigrationKey(version) {
    if (version.startsWith('0.1.')) {
        return '0.1.0';
    }
    if (version.startsWith('0.2.')) {
        return '0.2.0';
    }
    return 'legacy';
}
/**
 * Validate and normalize the data structure
 */
function validateAndNormalize(data) {
    try {
        const store = {
            version: data.version || '1.0.0',
            feedbacks: [],
            draft: undefined,
            settings: data.settings || {}
        };
        // Validate and normalize feedbacks
        if (Array.isArray(data.feedbacks)) {
            store.feedbacks = data.feedbacks
                .map(normalizeFeedback)
                .filter((f) => f !== null);
        }
        // Validate and normalize draft
        if (data.draft && typeof data.draft === 'object') {
            const draft = normalizeDraft(data.draft);
            store.draft = draft || undefined;
        }
        return store;
    }
    catch (error) {
        logger.error('Data normalization error:', error);
        return null;
    }
}
/**
 * Normalize a feedback object
 */
function normalizeFeedback(feedback) {
    try {
        if (!feedback || typeof feedback !== 'object') {
            return null;
        }
        // Required fields
        if (!feedback.id || !feedback.componentName || !feedback.comment || !feedback.timestamp) {
            return null;
        }
        const normalized = {
            id: String(feedback.id),
            componentName: String(feedback.componentName),
            componentPath: Array.isArray(feedback.componentPath) ? feedback.componentPath : [],
            comment: String(feedback.comment),
            screenshot: feedback.screenshot ? String(feedback.screenshot) : undefined,
            url: feedback.url ? String(feedback.url) : getCurrentUrl(),
            timestamp: String(feedback.timestamp),
            browserInfo: normalizeBrowserInfo(feedback.browserInfo),
            metadata: feedback.metadata && typeof feedback.metadata === 'object' ? feedback.metadata : {}
        };
        return normalized;
    }
    catch (error) {
        logger.warn('Failed to normalize feedback:', error);
        return null;
    }
}
/**
 * Normalize a draft object
 */
function normalizeDraft(draft) {
    try {
        if (!draft || typeof draft !== 'object') {
            return null;
        }
        if (!draft.componentInfo || !draft.comment) {
            return null;
        }
        return {
            componentInfo: {
                name: String(draft.componentInfo.name || 'Unknown'),
                path: Array.isArray(draft.componentInfo.path) ? draft.componentInfo.path : [],
                element: draft.componentInfo.element,
                props: draft.componentInfo.props || {},
                fiber: draft.componentInfo.fiber
            },
            comment: String(draft.comment),
            screenshot: draft.screenshot ? String(draft.screenshot) : undefined,
            createdAt: String(draft.createdAt || new Date().toISOString()),
            updatedAt: String(draft.updatedAt || new Date().toISOString())
        };
    }
    catch (error) {
        logger.warn('Failed to normalize draft:', error);
        return null;
    }
}
/**
 * Normalize browser info
 */
function normalizeBrowserInfo(browserInfo) {
    const defaultInfo = getDefaultBrowserInfo();
    if (!browserInfo || typeof browserInfo !== 'object') {
        return defaultInfo;
    }
    return {
        userAgent: String(browserInfo.userAgent || defaultInfo.userAgent),
        viewport: {
            width: Number(browserInfo.viewport?.width) || defaultInfo.viewport.width,
            height: Number(browserInfo.viewport?.height) || defaultInfo.viewport.height
        },
        platform: String(browserInfo.platform || defaultInfo.platform)
    };
}
/**
 * Migration from version 0.1.x
 */
function migrateFrom_0_1_0(data) {
    try {
        const defaultBrowserInfo = getDefaultBrowserInfo();
        const currentUrl = getCurrentUrl();
        return {
            version: '1.0.0',
            feedbacks: Array.isArray(data.items)
                ? data.items
                    .map((item) => ({
                    id: item.id || generateId(),
                    componentName: item.component || 'Unknown',
                    componentPath: [],
                    comment: item.text || item.comment || '',
                    screenshot: item.image,
                    url: item.url || currentUrl,
                    timestamp: item.date || item.timestamp || new Date().toISOString(),
                    browserInfo: defaultBrowserInfo
                }))
                    .filter((f) => f.comment)
                : [],
            settings: data.config || {}
        };
    }
    catch (error) {
        logger.error('0.1.0 migration failed:', error);
        return null;
    }
}
/**
 * Migration from version 0.2.x
 */
function migrateFrom_0_2_0(data) {
    try {
        const defaultBrowserInfo = getDefaultBrowserInfo();
        return {
            version: '1.0.0',
            feedbacks: Array.isArray(data.feedbacks)
                ? data.feedbacks.map((feedback) => ({
                    ...feedback,
                    id: feedback.id || generateId(),
                    componentPath: feedback.componentPath || [],
                    browserInfo: feedback.browserInfo || defaultBrowserInfo
                }))
                : [],
            draft: data.currentDraft
                ? {
                    componentInfo: data.currentDraft.component || {
                        name: 'Unknown',
                        path: [],
                        element: null
                    },
                    comment: data.currentDraft.comment || '',
                    screenshot: data.currentDraft.screenshot,
                    createdAt: data.currentDraft.created || new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                }
                : undefined,
            settings: data.settings || {}
        };
    }
    catch (error) {
        logger.error('0.2.0 migration failed:', error);
        return null;
    }
}
/**
 * Migration for legacy data (pre-versioning)
 */
function migrateLegacyData(data) {
    try {
        const defaultBrowserInfo = getDefaultBrowserInfo();
        const currentUrl = getCurrentUrl();
        // Handle array of feedback items (oldest format)
        if (Array.isArray(data)) {
            return {
                version: '1.0.0',
                feedbacks: data
                    .map((item) => ({
                    id: item.id || generateId(),
                    componentName: item.componentName || item.component || 'Unknown',
                    componentPath: item.path || [],
                    comment: item.comment || item.text || '',
                    screenshot: item.screenshot || item.image,
                    url: item.url || currentUrl,
                    timestamp: item.timestamp || item.date || new Date().toISOString(),
                    browserInfo: defaultBrowserInfo
                }))
                    .filter((f) => f.comment),
                settings: {}
            };
        }
        // Handle object with unknown structure
        if (data && typeof data === 'object') {
            const feedbacks = data.feedbacks || data.items || data.list || [];
            return {
                version: '1.0.0',
                feedbacks: Array.isArray(feedbacks)
                    ? feedbacks
                        .map(normalizeFeedback)
                        .filter((f) => f !== null)
                    : [],
                settings: data.settings || data.config || {}
            };
        }
        return null;
    }
    catch (error) {
        logger.error('Legacy migration failed:', error);
        return null;
    }
}
/**
 * Generate a unique ID for feedback items
 */
function generateId() {
    return `fb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
/**
 * Check if migration is needed
 */
function needsMigration(data, currentVersion) {
    if (!data) {
        return false;
    }
    if (!data.version) {
        return true;
    }
    return data.version !== currentVersion;
}
/**
 * Get migration info for a given data structure
 */
function getMigrationInfo(data, currentVersion) {
    return {
        hasData: !!data,
        sourceVersion: data?.version || 'legacy',
        targetVersion: currentVersion,
        needsMigration: needsMigration(data, currentVersion),
        migrationAvailable: !!getMigrationKey(data?.version || 'legacy')
    };
}

/**
 * MarkdownExporter - Generates markdown files from feedback data
 */
/**
 * Build a heading prefix from feedback type and severity.
 * Returns empty string when type is undefined (pre-phase-3 feedback).
 */
function buildTypePrefix$1(feedback) {
    if (!feedback.type)
        return '';
    const label = feedback.type.charAt(0).toUpperCase() + feedback.type.slice(1);
    if (feedback.severity) {
        const sevLabel = feedback.severity.charAt(0).toUpperCase() + feedback.severity.slice(1);
        return `[${label} - ${sevLabel}] `;
    }
    return `[${label}] `;
}
class MarkdownExporter {
    /**
     * Export feedback as markdown string
     */
    static exportAsMarkdown(feedbacks) {
        if (feedbacks.length === 0) {
            return '# Feedback Report\n\nNo feedback items found.\n';
        }
        const sortedFeedbacks = [...feedbacks].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        let markdown = this.generateHeader(sortedFeedbacks);
        markdown += '\n\n';
        markdown += this.generateFeedbackItems(sortedFeedbacks);
        return markdown;
    }
    /**
     * Generate download for markdown content
     */
    static downloadMarkdown(feedbacks, filename = 'feedback.md') {
        const markdown = this.exportAsMarkdown(feedbacks);
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Clean up the URL object
        URL.revokeObjectURL(url);
    }
    /**
     * Generate markdown header with summary
     */
    static generateHeader(feedbacks) {
        const timestamp = new Date().toISOString();
        const totalFeedback = feedbacks.length;
        const componentsCount = new Set(feedbacks.map((f) => f.componentName)).size;
        return [
            '# Feedback Report',
            `Generated on ${timestamp}`,
            '',
            '## Summary',
            `- **Total feedback items:** ${totalFeedback}`,
            `- **Components with feedback:** ${componentsCount}`
        ].join('\n');
    }
    /**
     * Generate all feedback items
     */
    static generateFeedbackItems(feedbacks) {
        return feedbacks
            .map((feedback, index) => this.generateFeedbackItem(feedback, index + 1))
            .join('\n\n---\n\n');
    }
    /**
     * Export a single feedback item without the report header/summary
     */
    static exportSingleItem(feedback) {
        return this.generateFeedbackItem(feedback);
    }
    /**
     * Generate a single feedback item
     */
    static generateFeedbackItem(feedback, index) {
        const timestamp = feedback.timestamp;
        const prefix = buildTypePrefix$1(feedback);
        const heading = index != null ? `${index}. ${prefix}${feedback.componentName}` : `${prefix}${feedback.componentName}`;
        let item = `## ${heading}\n\n`;
        // Feedback Comment
        item += '### Feedback\n';
        item += this.formatComment(feedback.comment);
        // Component Information
        item += '\n\n### Component Information\n';
        item += `- **Component:** ${feedback.componentName}\n`;
        item += `- **Path:** ${feedback.componentPath.join(' > ')}\n`;
        item += `- **URL:** ${feedback.url}\n`;
        item += `- **Timestamp:** ${timestamp}\n`;
        // Browser Information
        item += '\n### Browser Information\n';
        if (feedback.browserInfo.platform) {
            item += `- **Platform:** ${feedback.browserInfo.platform}\n`;
        }
        item += `- **Viewport:** ${feedback.browserInfo.viewport.width} x ${feedback.browserInfo.viewport.height}\n`;
        item += `- **User Agent:** ${feedback.browserInfo.userAgent}\n`;
        // HTML Snippet (if available)
        if (feedback.htmlSnippet) {
            item += '\n### HTML Snippet\n';
            item += '```html\n';
            item += feedback.htmlSnippet;
            item += '\n```\n';
        }
        // Metadata (if available)
        if (feedback.metadata && Object.keys(feedback.metadata).length > 0) {
            item += '\n### Additional Metadata\n';
            item += '```json\n';
            item += JSON.stringify(feedback.metadata, null, 2);
            item += '\n```\n';
        }
        return item;
    }
    /**
     * Format comment text for markdown
     */
    static formatComment(comment) {
        const escaped = comment
            .replace(/\\/g, '\\\\')
            .replace(/\*/g, '\\*')
            .replace(/_/g, '\\_')
            .replace(/~/g, '\\~')
            .replace(/`/g, '\\`');
        return escaped
            .split('\n')
            .map((line) => line.trim())
            .join('\n\n');
    }
    /**
     * Sanitize filename for download
     */
    static sanitizeFilename(filename) {
        return filename
            .replace(/[^\w\s.-]/g, '')
            .replace(/\s+/g, '_')
            .substring(0, 200);
    }
    /**
     * Generate suggested filename based on current date and feedback count
     */
    static generateFilename(feedbacks) {
        const date = new Date().toISOString().split('T')[0];
        const count = feedbacks.length;
        return `feedback_${date}_${count}items.md`;
    }
}

/**
 * ZipExporter - Creates ZIP archives with feedback data and images
 */
/**
 * Build a heading prefix from feedback type and severity.
 * Returns empty string when type is undefined (pre-phase-3 feedback).
 */
function buildTypePrefix(feedback) {
    if (!feedback.type)
        return '';
    const label = feedback.type.charAt(0).toUpperCase() + feedback.type.slice(1);
    if (feedback.severity) {
        const sevLabel = feedback.severity.charAt(0).toUpperCase() + feedback.severity.slice(1);
        return `[${label} - ${sevLabel}] `;
    }
    return `[${label}] `;
}
class ZipExporter {
    /**
     * Export feedback as ZIP archive
     */
    static async exportAsZip(feedbacks) {
        if (feedbacks.length === 0) {
            throw new Error('No feedback items to export');
        }
        const zip = new JSZip();
        // Add feedback.md (markdown with image references)
        const markdown = this.generateMarkdownWithImageRefs(feedbacks);
        zip.file('feedback.md', markdown);
        // Add feedback.json (complete data with base64 images)
        const jsonData = this.generateJsonData(feedbacks);
        zip.file('feedback.json', JSON.stringify(jsonData, null, 2));
        // Add images folder with extracted images
        const imagesFolder = zip.folder('images');
        if (!imagesFolder) {
            throw new Error('Failed to create images folder');
        }
        // Extract and add images
        await this.addImagesToZip(feedbacks, imagesFolder);
        // Generate the ZIP file
        return await zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: {
                level: 6
            }
        });
    }
    /**
     * Generate and download ZIP file
     */
    static async downloadZip(feedbacks, filename) {
        try {
            const zipBlob = await this.exportAsZip(feedbacks);
            const url = URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || this.generateZipFilename(feedbacks);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Clean up the URL object
            URL.revokeObjectURL(url);
        }
        catch (error) {
            logger.error('ZIP export failed:', error);
            throw new Error('Failed to generate ZIP export');
        }
    }
    /**
     * Generate markdown with image references instead of base64
     */
    static generateMarkdownWithImageRefs(feedbacks) {
        if (feedbacks.length === 0) {
            return '# Feedback Report\n\nNo feedback items found.\n';
        }
        const sortedFeedbacks = [...feedbacks].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        let markdown = this.generateHeaderWithImages(sortedFeedbacks);
        markdown += '\n\n';
        markdown += this.generateFeedbackItemsWithImages(sortedFeedbacks);
        return markdown;
    }
    /**
     * Generate header for ZIP markdown
     */
    static generateHeaderWithImages(feedbacks) {
        const timestamp = new Date().toISOString();
        const totalFeedback = feedbacks.length;
        const componentsCount = new Set(feedbacks.map((f) => f.componentName)).size;
        const imagesCount = feedbacks.filter((f) => f.screenshot).length;
        return [
            '# Feedback Report',
            `Generated on ${timestamp}`,
            '',
            '## Summary',
            `- **Total feedback items:** ${totalFeedback}`,
            `- **Components with feedback:** ${componentsCount}`,
            `- **Screenshots included:** ${imagesCount}`
        ].join('\n');
    }
    /**
     * Generate feedback items with image references
     */
    static generateFeedbackItemsWithImages(feedbacks) {
        return feedbacks
            .map((feedback, index) => this.generateFeedbackItemWithImages(feedback, index + 1))
            .join('\n\n---\n\n');
    }
    /**
     * Generate single feedback item with image references
     */
    static generateFeedbackItemWithImages(feedback, index) {
        const timestamp = feedback.timestamp;
        const prefix = buildTypePrefix(feedback);
        let item = `## ${index}. ${prefix}${feedback.componentName}\n\n`;
        // Screenshot FIRST (if available)
        if (feedback.screenshot) {
            const imageName = this.generateImageName(feedback);
            item += `![Screenshot of ${feedback.componentName}](images/${imageName})\n\n`;
        }
        // Feedback Comment
        item += '### Feedback\n';
        item += this.formatComment(feedback.comment);
        // Component Information
        item += '\n\n### Component Information\n';
        item += `- **Component:** ${feedback.componentName}\n`;
        item += `- **Path:** ${feedback.componentPath.join(' > ')}\n`;
        item += `- **URL:** ${feedback.url}\n`;
        item += `- **Timestamp:** ${timestamp}\n`;
        // Browser Information
        item += '\n### Browser Information\n';
        if (feedback.browserInfo.platform) {
            item += `- **Platform:** ${feedback.browserInfo.platform}\n`;
        }
        item += `- **Viewport:** ${feedback.browserInfo.viewport.width} x ${feedback.browserInfo.viewport.height}\n`;
        item += `- **User Agent:** ${feedback.browserInfo.userAgent}\n`;
        // HTML Snippet (if available)
        if (feedback.htmlSnippet) {
            item += '\n### HTML Snippet\n';
            item += '```html\n';
            item += feedback.htmlSnippet;
            item += '\n```\n';
        }
        // Metadata (if available)
        if (feedback.metadata && Object.keys(feedback.metadata).length > 0) {
            item += '\n### Additional Metadata\n';
            item += '```json\n';
            item += JSON.stringify(feedback.metadata, null, 2);
            item += '\n```\n';
        }
        return item;
    }
    /**
     * Generate JSON data with image paths instead of embedded data
     */
    static generateJsonData(feedbacks) {
        return {
            exportInfo: {
                generatedAt: new Date().toISOString(),
                generatedBy: 'Feedbacker Library',
                version: '1.0.0',
                format: 'Full ZIP Export',
                totalItems: feedbacks.length,
                itemsWithScreenshots: feedbacks.filter((f) => f.screenshot).length
            },
            feedbacks: feedbacks.map((feedback) => {
                const result = {
                    ...feedback,
                    screenshot: undefined
                };
                if (feedback.screenshot) {
                    result.exportedImagePath = `images/${this.generateImageName(feedback)}`;
                }
                delete result.screenshot;
                return result;
            })
        };
    }
    /**
     * Add images to ZIP archive
     */
    static async addImagesToZip(feedbacks, imagesFolder) {
        const imagePromises = feedbacks
            .filter((feedback) => feedback.screenshot)
            .map(async (feedback) => {
            const imageName = this.generateImageName(feedback);
            const imageData = this.extractImageData(feedback.screenshot);
            if (imageData) {
                imagesFolder.file(imageName, imageData, { base64: true });
            }
        });
        await Promise.all(imagePromises);
    }
    /**
     * Extract base64 image data from data URL
     */
    static extractImageData(dataUrl) {
        try {
            const matches = dataUrl.match(/^data:image\/[^;]+;base64,(.+)$/);
            return matches ? matches[1] : null;
        }
        catch (error) {
            logger.error('Failed to extract image data:', error);
            return null;
        }
    }
    /**
     * Generate image filename for a feedback item
     */
    static generateImageName(feedback) {
        const componentName = feedback.componentName
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .substring(0, 20);
        const timestamp = new Date(feedback.timestamp)
            .toISOString()
            .replace(/[:.]/g, '')
            .substring(0, 15);
        const shortId = feedback.id.slice(-6);
        let extension = 'png';
        if (feedback.screenshot?.includes('data:image/jpeg')) {
            extension = 'jpg';
        }
        else if (feedback.screenshot?.includes('data:image/webp')) {
            extension = 'webp';
        }
        return `${componentName}_${timestamp}_${shortId}.${extension}`;
    }
    /**
     * Format comment for markdown
     */
    static formatComment(comment) {
        const escaped = comment
            .replace(/\\/g, '\\\\')
            .replace(/\*/g, '\\*')
            .replace(/_/g, '\\_')
            .replace(/~/g, '\\~')
            .replace(/`/g, '\\`');
        return escaped
            .split('\n')
            .map((line) => line.trim())
            .join('\n\n');
    }
    /**
     * Generate ZIP filename
     */
    static generateZipFilename(feedbacks) {
        const date = new Date().toISOString().split('T')[0];
        const count = feedbacks.length;
        return `feedback_${date}_${count}items.zip`;
    }
    /**
     * Sanitize filename for download
     */
    static sanitizeFilename(filename) {
        return filename
            .replace(/[^\w\s.-]/g, '')
            .replace(/\s+/g, '_')
            .substring(0, 200);
    }
}

export { ErrorType, FeedbackEventEmitter, LogLevel, Logger, MarkdownExporter, ZipExporter, captureHtmlSnippet, escapeHtml, formatDate, formatDateForStorage, formatDistanceToNow, formatHtmlSnippet, getMigrationInfo, isValidComment, isValidDraft, isValidFeedback, isValidScreenshot, logger, migrateData, needsMigration, sanitizeArray, sanitizeBrowserInfo, sanitizeComment, sanitizeComponentInfo, sanitizeDataUrl, sanitizeDraft, sanitizeFeedback, sanitizeFilename, sanitizeMetadata, sanitizeNumber, sanitizeProps, sanitizeString, sanitizeTimestamp, sanitizeUrl, stripHtmlTags, validateBrowserInfo, validateComment, validateComponentInfo, validateDraft, validateFeedback, validateFeedbackId, validateScreenshot, validateStorageData };
//# sourceMappingURL=index.esm.js.map
